/**
 * Форматирование чисел, дат и русских слов. Перенесено из макета (dashbdv3.html, JS 1420–1449)
 * один в один: те же формулы, те же разделители.
 *
 * Даты. Бэкенд отдаёт ISO-строки со смещением клиники (`2026-09-28T00:00:00.000+03:00`).
 * Мы печатаем ровно те цифры, что стоят в строке, а не пересчитываем их в таймзону браузера:
 * иначе у пользователя не в Москве срок «28.09» превратился бы в «27.09». Для `Date` и числа
 * берутся локальные компоненты — такие значения появляются только когда фронт сам создаёт дату.
 */

export type DateLike = string | number | Date | null | undefined;

export interface WallClock {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

function fromDate(date: Date): WallClock {
    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds(),
    };
}

/** Разбирает значение в «настенное время» — то, что показываем пользователю. */
export function wallClock(value: DateLike): WallClock | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string') {
        const m = ISO_RE.exec(value);
        if (m) {
            return {
                year: Number(m[1]),
                month: Number(m[2]),
                day: Number(m[3]),
                hour: m[4] ? Number(m[4]) : 0,
                minute: m[5] ? Number(m[5]) : 0,
                second: m[6] ? Number(m[6]) : 0,
            };
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : fromDate(parsed);
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : fromDate(date);
}

export function pad2(n: number): string {
    return n < 10 ? `0${n}` : String(n);
}

/** `ДД.ММ.ГГГГ`; пустая строка, если даты нет. */
export function fmtDate(value: DateLike): string {
    const w = wallClock(value);
    if (!w) return '';
    return `${pad2(w.day)}.${pad2(w.month)}.${w.year}`;
}

/** `ДД.ММ` */
export function fmtShort(value: DateLike): string {
    const w = wallClock(value);
    if (!w) return '';
    return `${pad2(w.day)}.${pad2(w.month)}`;
}

/** `ГГГГ-ММ-ДД` — значение для `<input type="date">` и параметров `from`/`to`. */
export function fmtISO(value: DateLike): string {
    const w = wallClock(value);
    if (!w) return '';
    return `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
}

/** `ЧЧ:ММ` */
export function fmtTime(value: DateLike): string {
    const w = wallClock(value);
    if (!w) return '';
    return `${pad2(w.hour)}:${pad2(w.minute)}`;
}

/** `ДД.ММ.ГГГГ · ЧЧ:ММ` — формат строки `.ev-time` в истории дела. */
export function fmtDateTime(value: DateLike): string {
    const w = wallClock(value);
    if (!w) return '';
    return `${pad2(w.day)}.${pad2(w.month)}.${w.year} · ${pad2(w.hour)}:${pad2(w.minute)}`;
}

/** `ДД.ММ ЧЧ:ММ` — компактный вариант для лент. */
export function fmtShortTime(value: DateLike): string {
    const w = wallClock(value);
    if (!w) return '';
    return `${pad2(w.day)}.${pad2(w.month)} ${pad2(w.hour)}:${pad2(w.minute)}`;
}

/** Русская плюрализация (макет, строка 1441). */
export function plural(n: number, one: string, few: string, many: string): string {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return many;
    if (b > 1 && b < 5) return few;
    if (b === 1) return one;
    return many;
}

export function dayWord(n: number): string {
    return plural(n, 'день', 'дня', 'дней');
}

export function caseWord(n: number): string {
    return plural(n, 'дело', 'дела', 'дел');
}

export function appealWord(n: number): string {
    return plural(n, 'обращение', 'обращения', 'обращений');
}

export function itemWord(n: number): string {
    return plural(n, 'пункт', 'пункта', 'пунктов');
}

/** Округление до 0,1 с запятой в качестве разделителя: `7 → "7"`, `7.45 → "7,5"`. */
export function num1(x: number | null | undefined): string {
    if (x === null || x === undefined || Number.isNaN(x)) return '—';
    return (Math.round(x * 10) / 10).toString().replace('.', ',');
}

/** Разряды неразрывным пробелом: `127500 → "127 500"`. */
export function money(n: number | null | undefined): string {
    if (n === null || n === undefined || Number.isNaN(n)) return '—';
    return n.toLocaleString('ru-RU');
}

/** Целая доля в процентах для инлайновой ширины полос. */
export function pct(value: number, max: number): number {
    if (!max || max <= 0) return 0;
    return Math.round((value / max) * 100);
}

/** Неокруглённая доля — только для сегментов `.bar-track` (макет, строка 1944). */
export function pctExact(value: number, total: number): number {
    if (!total || total <= 0) return 0;
    return (value / total) * 100;
}

/** `«Иванов Иван Иванович» → «Иванов И.И.»`; уже сокращённое ФИО возвращается как есть. */
export function shortName(fullName: string | null | undefined): string {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length < 2) return fullName.trim();
    // Имя пользователя — должность («Главный врач», «Заместитель главного врача по …»): не ФИО, не сокращается.
    if (!parts.every((p) => /^[А-ЯЁA-Z]/u.test(p))) return fullName.trim();
    const initials = parts
        .slice(1)
        .map((p) => (p.endsWith('.') ? p : `${p.charAt(0).toUpperCase()}.`))
        .join('');
    return `${parts[0]} ${initials}`;
}

/** Инициалы для кружка аватара, если сервер их не прислал. */
export function initialsOf(fullName: string | null | undefined): string {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/);
    const letters = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
    return letters.join('');
}

/** Размер вложения: `128000 → "125 КБ"`. */
export function fileSize(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${num1(bytes / (1024 * 1024))} МБ`;
}

export function truncate(text: string, limit: number): string {
    if (text.length <= limit) return text;
    return `${text.slice(0, Math.max(0, limit - 1)).trimEnd()}…`;
}

export type DeltaDirection = 'up' | 'down' | 'flat';

export interface DeltaParts {
    /** Абсолютная разница `cur - prev`, всегда штуки, никогда не проценты. */
    value: number;
    direction: DeltaDirection;
    /** Крупный вариант: `▲ +3`, `▼ -2`, `➡ 0` (макет, `deltaHtml`). */
    text: string;
    /** Компактный вариант: `+3`, `-2`, `0` (макет, `deltaSmall`). */
    shortText: string;
}

/**
 * Направление ИНВЕРТИРОВАНО по смыслу: рост числа жалоб — плохо, поэтому `up` красный,
 * а `down` зелёный. Цвет задают классы `.up`/`.down`/`.flat`, см. Badges.tsx.
 */
export function delta(cur: number, prev: number): DeltaParts {
    const value = cur - prev;
    if (value > 0) return { value, direction: 'up', text: `▲ +${value}`, shortText: `+${value}` };
    if (value < 0) return { value, direction: 'down', text: `▼ ${value}`, shortText: `${value}` };
    return { value: 0, direction: 'flat', text: '➡ 0', shortText: '0' };
}
